import { AGENT_CONTEXT_SCHEMA, readAgentContext } from "./agentContext";
import type { FocusTask } from "./personalFocus";
import { parseTaskReportOrigin } from "./taskReportOrigin";
import type { Doc } from "./types";

export interface FocusEvidence {
  key: string;
  kind: "ticket" | "doc" | "external";
  label: string;
  href: string;
  agentProvided: boolean;
}

/** Supplied only after the caller authorized the relation under the viewer's RLS. */
export interface AuthorizedFocusSourceRelation {
  workspaceId: string;
  issueId: string;
  sourceType: "doc" | "note";
  sourceId: string;
}

export interface FocusDependency {
  /** Empty for an unresolved reference: never disclose a foreign cached identity. */
  id: string;
  identifier: string | null;
  state: "open" | "closed" | "unverified";
}

type VisibleDoc = Pick<Doc, "id" | "workspaceId" | "title">;
type RecordValue = Record<string, unknown>;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_REFERENCES = 50;
const AGENT_LABEL = "Referência registrada por agente; confira na fonte";

function record(value: unknown): RecordValue | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as RecordValue : null;
}

function uuid(value: unknown): string | null {
  return typeof value === "string" && UUID.test(value) ? value.toLowerCase() : null;
}

function context(task: FocusTask): RecordValue | null {
  // The writer already limits these blocks to 64 KiB. Bound parsing for older
  // or hand-written bodies as well, and require the known context schema.
  if (task.description.length > 1_000_000) return null;
  const value = readAgentContext(task.description);
  return value?.schema === AGENT_CONTEXT_SCHEMA ? value : null;
}

function ownTask(task: FocusTask, visibleTasks: readonly FocusTask[]): FocusTask | null {
  return visibleTasks.find((candidate) => candidate.id === task.id && candidate.workspaceId === task.workspaceId) ?? null;
}

function canonicalHref(workspaceSlug: string, kind: "issue" | "doc", id: string): string | null {
  if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,99}$/.test(workspaceSlug) || !uuid(id)) return null;
  return `/${encodeURIComponent(workspaceSlug)}?${kind}=${id.toLowerCase()}`;
}

/** Link validation does not fetch the source or establish permission to read it. */
function externalUrl(value: unknown): URL | null {
  const parsed = parseTaskReportOrigin({ report_channel: "other", report_url: value });
  if (!parsed.ok || !parsed.value.report_url) return null;
  const url = new URL(parsed.value.report_url);
  // Signed/access-bearing query parameters must not be republished by this UI.
  for (const key of url.searchParams.keys()) {
    let decoded = key;
    try {
      for (let pass = 0; pass < 3; pass++) {
        const next = decodeURIComponent(decoded);
        if (next === decoded) break;
        decoded = next;
      }
    } catch { return null; }
    const name = decoded.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (decoded.includes("%") || /(token|secret|signature|credential|password|passwd|authorization|apikey|accesskey|resourcekey|rlkey)/.test(name) ||
      ["key", "sig", "auth", "pwd", "code", "jwt"].includes(name)) return null;
  }
  // OAuth fragments can carry the same secrets and are not source locators.
  let fragment = url.hash.slice(1);
  try {
    for (let pass = 0; pass < 3; pass++) {
      const decoded = decodeURIComponent(fragment);
      if (decoded === fragment) break;
      fragment = decoded;
    }
  } catch { return null; }
  if (/(?:^|[&#])(access_token|id_token|token|code|password|secret|sig|signature)=/i.test(fragment)) return null;
  return url;
}

function provider(url: URL): string {
  const host = url.hostname.toLowerCase();
  if (host === "slack.com" || host.endsWith(".slack.com")) return "Slack";
  if (host === "drive.google.com" || host === "docs.google.com") return "Google Drive";
  if (host === "gemini.google.com") return "Gemini";
  if (host === "loom.com" || host === "www.loom.com") return "Loom";
  if (host === "github.com") return "GitHub";
  return "Link externo";
}

type InternalReference = { kind: "doc" | "issue"; id: string | null };

function consistentId(value: RecordValue, fields: readonly string[]): string | null {
  const supplied = fields.filter((field) => value[field] !== undefined).map((field) => uuid(value[field]));
  return supplied.length > 0 && supplied.every((id) => id !== null && id === supplied[0]) ? supplied[0] : null;
}

function internalReference(value: RecordValue): InternalReference | null {
  const type = value.type ?? value.kind;
  if (value.doc_id !== undefined || value.document_id !== undefined || type === "doc" || type === "document") {
    return { kind: "doc", id: value.issue_id !== undefined || value.task_id !== undefined ||
      (value.type !== undefined && value.kind !== undefined && value.type !== value.kind)
      ? null : consistentId(value, ["doc_id", "document_id", "id"]) };
  }
  if (value.issue_id !== undefined || value.task_id !== undefined || type === "issue" || type === "task") {
    return { kind: "issue", id: value.type !== undefined && value.kind !== undefined && value.type !== value.kind
      ? null : consistentId(value, ["issue_id", "task_id", "id"]) };
  }
  return null;
}

/** Only explicit ticket metadata and supplied authorized entities are usable.
 * Source labels/excerpts/claimed permissions are deliberately never copied.
 * The caller must supply its current viewer's live workspace inventory.
 */
export function getPersonalFocusEvidence(
  task: FocusTask,
  workspaceSlug: string,
  visibleDocs: readonly VisibleDoc[],
  visibleTasks: readonly FocusTask[],
  authorizedSourceRelations: readonly AuthorizedFocusSourceRelation[] = [],
): FocusEvidence[] {
  const current = ownTask(task, visibleTasks);
  const ticketHref = canonicalHref(workspaceSlug, "issue", task.id);
  if (!current || !ticketHref) return [];
  const evidence: FocusEvidence[] = [{
    key: `ticket:${current.id}`, kind: "ticket", label: "Dados registrados da tarefa",
    href: ticketHref, agentProvided: false,
  }];
  const seen = new Set([ticketHref]);
  function add(item: FocusEvidence) {
    if (!seen.has(item.href)) { seen.add(item.href); evidence.push(item); }
  }
  function addInternal(ref: InternalReference, agentProvided: boolean) {
    if (!ref.id) return;
    const entity = (ref.kind === "doc" ? visibleDocs : visibleTasks)
      .find((candidate) => candidate.id === ref.id && candidate.workspaceId === current!.workspaceId);
    if (!entity) return;
    const href = canonicalHref(workspaceSlug, ref.kind, entity.id);
    if (!href) return;
    const label = agentProvided ? AGENT_LABEL : `${ref.kind === "doc" ? "Documento" : "Tarefa"} de referência registrada`;
    add({ key: `${ref.kind}:${entity.id}`, kind: ref.kind === "doc" ? "doc" : "ticket", label, href, agentProvided });
  }
  function addUrl(value: unknown, agentProvided: boolean) {
    const url = externalUrl(value);
    if (!url) return;
    // A canonical entity query is never a way around the supplied inventory,
    // even if it was recorded as an external link by a producer.
    const docs = url.searchParams.getAll("doc");
    const issues = url.searchParams.getAll("issue");
    if (docs.length || issues.length) {
      if (docs.length + issues.length !== 1 || url.pathname !== `/${workspaceSlug}`) return;
      addInternal({ kind: docs.length ? "doc" : "issue", id: uuid(docs[0] ?? issues[0]) }, agentProvided);
      return;
    }
    const prefix = agentProvided ? AGENT_LABEL : "Origem registrada";
    add({ key: `external:${url.href}`, kind: "external", href: url.href,
      label: `${prefix} · ${provider(url)} · Abrir referência registrada`, agentProvided });
  }

  const origin = parseTaskReportOrigin({ report_channel: current.reportChannel, report_url: current.reportUrl });
  if (origin.ok && origin.value.report_url) addUrl(origin.value.report_url, false);
  for (const relation of authorizedSourceRelations.slice(0, MAX_REFERENCES)) {
    if (relation.workspaceId === current.workspaceId && relation.issueId === current.id && relation.sourceType === "doc") {
      addInternal({ kind: "doc", id: uuid(relation.sourceId) }, false);
    }
  }
  const machineContext = context(current);
  const refs = machineContext?.source_refs;
  if (Array.isArray(refs)) for (const candidate of refs.slice(0, MAX_REFERENCES)) {
    const ref = record(candidate);
    if (!ref) continue;
    const internal = internalReference(ref);
    if (internal) addInternal(internal, true);
    else if (ref.url !== undefined) addUrl(ref.url, true);
  }
  // humanFirstDocumentation writes this explicit v1 relationship array. Read
  // that stored metadata only; never repeat its producer's Markdown extraction.
  const urls = record(machineContext?.relationships)?.urls;
  if (Array.isArray(urls)) for (const candidate of urls.slice(0, MAX_REFERENCES)) {
    addUrl(typeof candidate === "string" ? candidate : record(candidate)?.url, true);
  }
  return evidence;
}

/** Dependencies are producer assertions, not instructions or inferred prose.
 * Their current state is read only from the authorized workspace inventory.
 */
export function getExplicitFocusDependencies(task: FocusTask, visibleTasks: readonly FocusTask[]): FocusDependency[] {
  const current = ownTask(task, visibleTasks);
  if (!current) return [];
  const dependencies = context(current)?.dependencies;
  const value = record(dependencies);
  const candidates = Array.isArray(dependencies) ? dependencies : value
    ? [value.issue_ids, value.blocked_by, value.depends_on].flatMap((item) => Array.isArray(item) ? item : []) : [];
  const result: FocusDependency[] = [];
  const seen = new Set<string>();
  for (const candidate of candidates.slice(0, MAX_REFERENCES)) {
    const ref = record(candidate);
    if (ref && ref.type !== undefined && ref.type !== "issue" && ref.type !== "task") continue;
    if (ref && ref.kind !== undefined && ref.kind !== "issue" && ref.kind !== "task") continue;
    const id = ref ? consistentId(ref, ["issue_id", "task_id", "id"]) : uuid(candidate);
    if (!id && ref && [ref.id, ref.issue_id, ref.task_id].some((value) => uuid(value))) {
      if (!result.some((item) => item.state === "unverified")) result.push({ id: "", identifier: null, state: "unverified" });
    }
    if (!id || id === current.id || seen.has(id)) continue;
    seen.add(id);
    const target = visibleTasks.find((item) => item.id === id && item.workspaceId === current.workspaceId);
    if (!target) {
      if (!result.some((item) => item.state === "unverified")) result.push({ id: "", identifier: null, state: "unverified" });
      continue;
    }
    result.push({ id, identifier: target.identifier || null,
      state: target.status === "done" || target.status === "canceled" ? "closed" : "open" });
  }
  if (candidates.length > MAX_REFERENCES && !result.some((item) => item.state === "unverified")) {
    result.push({ id: "", identifier: null, state: "unverified" });
  }
  return result;
}
